import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Leaf, BookOpen, PenLine, Trees, Feather, Play, Award } from "lucide-react";
import { Reveal, MaskedLines, Eyebrow } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import Newsletter from "@/components/Newsletter";
import TestimonialWall from "@/components/TestimonialWall";
import SubstackSection from "@/components/SubstackSection";
import { BOOKS, HANDBOOK, MANIFESTO, MARQUEE_ITEMS, WANGARI_QUOTE, INTERVIEWS, VIDEOS } from "@/data/content";

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yPhoto = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center overflow-hidden bg-paper grain" data-testid="home-hero">
      <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-goldsoft blur-3xl opacity-70" aria-hidden="true" />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 w-full grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center pt-28 pb-20 lg:py-24 relative z-10">
        <motion.div style={{ y: yText }} className="lg:col-span-7">
          <Eyebrow className="mb-6">Author · Educator · Speaker — Chicago</Eyebrow>
          <h1>
            <MaskedLines
              delay={0.2}
              lines={[
                "Planting seeds of",
                "literacy, ecology",
                <span key="l3">& <em className="text-terra">wonder.</em></span>,
              ]}
              className="font-serif text-[13vw] sm:text-7xl lg:text-[5.6rem] text-ink leading-[1.02] tracking-tight block"
            />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 text-txtmut text-base sm:text-lg leading-relaxed max-w-xl"
          >
            Jen Cullerton Johnson is an award-winning children's author and educator working at the
            intersection of literacy and environmental learning — author of{" "}
            <em className="font-serif text-ink">Seeds of Change</em> and{" "}
            <em className="font-serif text-ink">The Green Literacy Handbook</em>.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <Link
              to="/collaborate"
              className="group inline-flex items-center justify-center gap-2 bg-terra hover:bg-terradark text-paper rounded-full px-8 py-4 font-medium transition-colors duration-300"
              data-testid="hero-primary-cta"
            >
              Book an Author Visit
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/books"
              className="inline-flex items-center justify-center gap-2 border border-ink/25 hover:border-ink text-ink rounded-full px-8 py-4 font-medium transition-colors duration-300"
              data-testid="hero-secondary-cta"
            >
              Explore Books
            </Link>
          </motion.div>
        </motion.div>

        <motion.div style={{ y: yPhoto }} className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="rounded-t-[999px] rounded-b-2xl overflow-hidden border border-line shadow-2xl shadow-ink/10">
              <img
                src="/images/jen-photo-2.jpg"
                alt="Jen Cullerton Johnson celebrating Seeds of Change with a young reader"
                className="w-full h-[420px] sm:h-[520px] object-cover photo-tone"
                data-testid="hero-photo"
              />
            </div>
            <div className="absolute -left-6 sm:-left-10 bottom-10 w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-paper border border-line shadow-lg flex items-center justify-center animate-float-soft" aria-hidden="true">
              <svg viewBox="0 0 120 120" className="w-full h-full animate-spin-slow">
                <defs>
                  <path id="circ" d="M60,60 m-44,0 a44,44 0 1,1 88,0 a44,44 0 1,1 -88,0" fill="none" />
                </defs>
                <text fontSize="10" letterSpacing="2.6" fill="#0F3832" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                  <textPath href="#circ">MEET THE AUTHOR · MEET THE AUTHOR ·</textPath>
                </text>
              </svg>
              <Leaf className="absolute w-6 h-6 text-terra" strokeWidth={1.5} />
            </div>
            <div className="absolute -bottom-5 right-4 sm:right-8 bg-ink text-paper rounded-lg px-5 py-4 shadow-xl flex items-center gap-3" data-testid="hero-award-card">
              <Award className="w-5 h-5 text-gold shrink-0" />
              <p className="text-xs leading-snug">
                Bronze FORWARD <span className="block font-serif italic text-sm">Indies Award Winner</span>
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function Positioning() {
  return (
    <section className="bg-paper py-20 lg:py-32" data-testid="home-positioning">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <Reveal className="lg:col-span-7">
            <Eyebrow className="mb-6">The work</Eyebrow>
            <h2 className="font-serif text-3xl sm:text-5xl text-ink leading-[1.12] tracking-tight text-balance">
              Where literacy meets the living world — and young readers meet their own power.
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <p className="text-txtmut leading-relaxed">
              With nearly two decades in education — from Chicago Public Schools to classrooms abroad — Jen
              writes true stories that grow critical thinkers, careful readers, and courageous caretakers of the
              planet.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-6" data-testid="home-stats">
              {[
                ["8", "book awards & honors"],
                ["Pre-K–8", "audience for visits"],
                ["2", "decades teaching"],
              ].map(([n, l]) => (
                <div key={l}>
                  <p className="font-serif text-3xl sm:text-4xl text-terra">{n}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-txtmut mt-2 leading-relaxed">{l}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function HandbookSpotlight() {
  return (
    <section className="bg-cream border-y border-line py-20 lg:py-32 relative grain" data-testid="home-handbook">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative z-10">
        <Reveal className="lg:col-span-5">
          <div className="relative max-w-sm mx-auto">
            <div className="absolute inset-0 translate-x-4 translate-y-4 border border-terra/40 rounded-lg" aria-hidden="true" />
            <div className="relative rounded-lg overflow-hidden shadow-2xl shadow-ink/15 border border-line">
              <img src={HANDBOOK.image} alt="The Green Literacy Handbook, by Jen Cullerton Johnson" className="w-full h-[440px] sm:h-[520px] object-cover photo-tone" data-testid="handbook-photo" />
            </div>
            <div className="absolute -top-4 -right-4 bg-gold text-ink font-mono text-[10px] uppercase tracking-[0.14em] rounded-full px-4 py-2 shadow-md" data-testid="handbook-award-badge">
              {HANDBOOK.award}
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-7">
          <Eyebrow className="mb-6">New from Solution Tree Press</Eyebrow>
          <h2 className="font-serif text-4xl sm:text-5xl text-ink leading-[1.08] mb-6">{HANDBOOK.title}</h2>
          <p className="text-txtmut text-lg leading-relaxed max-w-xl mb-8">{HANDBOOK.description}</p>
          <div className="flex flex-wrap gap-4 items-center">
            <a
              href={HANDBOOK.buyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 bg-ink hover:bg-pine text-paper rounded-full px-8 py-4 font-medium transition-colors duration-300"
              data-testid="handbook-buy-button"
            >
              Buy the Handbook
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <Link to="/books" className="link-underline font-mono text-xs uppercase tracking-[0.18em] text-terra" data-testid="handbook-view-all-link">
              View all books
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Manifesto() {
  return (
    <section className="bg-paper py-20 lg:py-32" data-testid="home-manifesto">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <Reveal>
          <Eyebrow className="mb-6">A practice in three chapters</Eyebrow>
          <h2 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight mb-16 lg:mb-20">What Jen believes</h2>
        </Reveal>
        <div className="space-y-0 border-t border-line">
          {MANIFESTO.map((m, i) => (
            <Reveal key={m.num} delay={i * 0.08}>
              <div className="group grid grid-cols-12 gap-4 sm:gap-8 py-10 lg:py-14 border-b border-line items-start" data-testid={`manifesto-chapter-${i + 1}`}>
                <p className="col-span-2 sm:col-span-1 font-mono text-sm sm:text-base text-terra pt-2">{m.num}</p>
                <h3 className="col-span-10 sm:col-span-5 font-serif text-2xl sm:text-4xl text-ink leading-tight transition-colors duration-300 group-hover:text-terra">
                  {m.title}
                </h3>
                <p className="col-span-12 sm:col-span-6 sm:col-start-7 text-txtmut leading-relaxed">{m.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function BooksPreview() {
  return (
    <section className="bg-cream border-y border-line py-20 lg:py-32" data-testid="home-books">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <Reveal className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14">
          <div>
            <Eyebrow className="mb-6">Books</Eyebrow>
            <h2 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight">True stories, growing readers</h2>
          </div>
          <Link to="/books" className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-terra link-underline shrink-0" data-testid="home-books-view-all">
            All publications <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {BOOKS.map((b, i) => (
            <Reveal key={b.id} delay={i * 0.08}>
              <Link to="/books" className="group block" data-testid={`home-book-card-${b.id}`}>
                <div className="rounded-lg overflow-hidden border border-line bg-paper shadow-sm group-hover:shadow-xl group-hover:shadow-ink/10 group-hover:-translate-y-2 transition-all duration-500">
                  <div className="aspect-[4/5] overflow-hidden">
                    <img src={b.image} alt={`Cover of ${b.title}`} className="w-full h-full object-cover photo-tone group-hover:scale-[1.04] transition-transform duration-700" />
                  </div>
                </div>
                <p className="font-serif text-base sm:text-lg text-ink mt-4 leading-snug">{b.title}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-txtmut mt-1">{b.publisher}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const SERVICES = [
  { icon: BookOpen, title: "Author Visits", desc: "Energetic, interactive school visits with read-alouds in English or Spanish, Q&A, tree planting or eco-art, and signed book sales. Grades Pre-K–8.", cta: "Plan a visit" },
  { icon: UsersIcon, title: "Professional Development", desc: "Green Literacy workshops for educators and community leaders — online and in person — integrating reading, writing, and environmental action.", cta: "Request a workshop" },
  { icon: PenLine, title: "Creative Coaching", desc: "One-on-one sessions for writers, educators, artists, and activists. 30 or 60 minutes on Zoom or phone — one-time or ongoing.", cta: "Book a session" },
  { icon: Trees, title: "Forest Bathing", desc: "Guided nature immersion walks in Chicago's parks and the Indiana Dunes — slow, sensory, and restorative.", cta: "Join a walk" },
];

function UsersIcon(props) {
  return <Feather {...props} />;
}

function CollaboratePreview() {
  return (
    <section className="bg-paper py-20 lg:py-32" data-testid="home-collaborate">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <Reveal>
          <Eyebrow className="mb-6">Collaborate</Eyebrow>
          <h2 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight mb-6">Inspire learners. Empower community.</h2>
          <p className="text-txtmut max-w-xl leading-relaxed mb-14">
            Bring books to life — with the author in the room. Every visit, workshop, and walk is tailored to your community.
          </p>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.08}>
              <Link to="/collaborate" className="group block h-full border border-line rounded-lg bg-paper hover:bg-cream p-8 lg:p-10 transition-colors duration-300" data-testid={`home-service-card-${i + 1}`}>
                <div className="flex items-start justify-between">
                  <span className="w-11 h-11 rounded-full bg-goldsoft text-terra flex items-center justify-center group-hover:bg-terra group-hover:text-paper transition-colors duration-300">
                    <s.icon className="w-5 h-5" strokeWidth={1.5} />
                  </span>
                  <ArrowUpRight className="w-5 h-5 text-txtmut/40 group-hover:text-terra group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300" />
                </div>
                <h3 className="font-serif text-2xl text-ink mt-6 mb-3">{s.title}</h3>
                <p className="text-txtmut text-sm leading-relaxed mb-5">{s.desc}</p>
                <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-terra link-underline">{s.cta}</span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ForestSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  return (
    <section ref={ref} className="relative overflow-hidden" data-testid="home-forest">
      <motion.div style={{ y }} className="absolute inset-0 scale-[1.25]">
        <img src="/images/forest-walk.jpg" alt="Sunlight through forest canopy" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-ink/70" aria-hidden="true" />
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-28 lg:py-40">
        <Reveal className="max-w-2xl">
          <Eyebrow className="mb-6 text-gold">Forest bathing — with a certified guide</Eyebrow>
          <h2 className="font-serif text-4xl sm:text-6xl text-paper leading-[1.05] mb-6 text-balance">
            "When we step into the forest, we step closer to ourselves."
          </h2>
          <p className="text-paper/75 leading-relaxed mb-10 max-w-xl">
            Certified by the Forest Therapy School in 2025, Jen guides monthly walks in parks and preserves
            throughout the Chicago area and at the Indiana Dunes — gentle meditations, mindful exploration, and
            community connection.
          </p>
          <Link
            to="/collaborate"
            className="inline-flex items-center gap-2 bg-paper text-ink hover:bg-goldsoft rounded-full px-8 py-4 font-medium transition-colors duration-300"
            data-testid="forest-cta"
          >
            Book a forest session <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function WangariQuote() {
  return (
    <section className="bg-ink text-paper py-24 lg:py-32 relative grain" data-testid="home-wangari-quote">
      <div className="max-w-4xl mx-auto px-4 sm:px-8 text-center relative z-10">
        <Reveal>
          <Leaf className="w-8 h-8 text-gold mx-auto mb-8" strokeWidth={1.2} />
          <blockquote className="font-serif italic text-3xl sm:text-4xl lg:text-5xl leading-[1.2] text-balance">
            "{WANGARI_QUOTE.text}"
          </blockquote>
          <p className="mt-8 font-mono text-xs uppercase tracking-[0.22em] text-gold">— {WANGARI_QUOTE.author}</p>
        </Reveal>
      </div>
    </section>
  );
}

function PressSection() {
  return (
    <section className="bg-paper py-20 lg:py-32" data-testid="home-press">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
        <Reveal>
          <Eyebrow className="mb-6">Press & media</Eyebrow>
          <h2 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight mb-8">Jen in the wild</h2>
          <div className="relative rounded-lg overflow-hidden border border-line group cursor-pointer" data-testid="press-video-card">
            <a href={VIDEOS.ksnt} target="_blank" rel="noopener noreferrer" aria-label="Watch: Children's book author comes to Kansas Children's Discovery Center (KSNT News)" data-testid="press-video-link">
              <img src="/images/event-discovery-center.jpg" alt="Jen Cullerton Johnson reading to students at an event" className="w-full h-72 sm:h-80 object-cover photo-tone group-hover:scale-[1.03] transition-transform duration-700" />
              <div className="absolute inset-0 bg-ink/30 flex items-center justify-center">
                <span className="w-16 h-16 rounded-full bg-paper text-ink flex items-center justify-center group-hover:bg-terra group-hover:text-paper transition-colors duration-300">
                  <Play className="w-6 h-6 fill-current" />
                </span>
              </div>
              <p className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-ink/80 to-transparent text-paper text-sm p-5">
                Children's book author comes to Kansas Children's Discovery Center — KSNT News
              </p>
            </a>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-txtmut mb-6">Interviews & conversations</p>
          <ul className="divide-y divide-line border-y border-line">
            {INTERVIEWS.map((it) => (
              <li key={it.url}>
                <a href={it.url} target="_blank" rel="noopener noreferrer" className="group flex items-center justify-between gap-4 py-5" data-testid="press-interview-link">
                  <span>
                    <span className="block font-serif text-lg text-ink group-hover:text-terra transition-colors duration-300">{it.name}</span>
                    <span className="block font-mono text-[10px] uppercase tracking-[0.16em] text-txtmut mt-1">{it.pub}</span>
                  </span>
                  <ArrowUpRight className="w-5 h-5 text-txtmut/40 group-hover:text-terra group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300 shrink-0" />
                </a>
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  useEffect(() => {
    document.title = "Jen Cullerton Johnson — Author, Educator & Speaker";
  }, []);
  return (
    <>
      <Hero />
      <Marquee items={MARQUEE_ITEMS} />
      <Positioning />
      <HandbookSpotlight />
      <Manifesto />
      <BooksPreview />
      <TestimonialWall />
      <CollaboratePreview />
      <ForestSection />
      <WangariQuote />
      <PressSection />
      <SubstackSection />
      <Newsletter />
    </>
  );
}
